import {accountUserApi} from '@api/accountUser/api';
import {getResponseBody} from '@utils/apiResponse';
import type {AccountUserResource} from '@api/accountUser/api.d';

interface AccountIdSource {
    activeAccountId?: string | null;
    account?: {id?: string} | null;
}

export const getActiveAccountId = (userState: AccountIdSource): string => (
    userState.activeAccountId || userState.account?.id || ''
);

/** Membership status is the single source of truth: `pending` is invited, `active` registered, `inactive` locked. */
export const getIsInvitedAccountUser = (accountUser: AccountUserResource): boolean => (
    accountUser.status === 'pending'
);

const getParsedUserIds = (raw: unknown): string[] => {
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === 'string' && raw) return raw.split(',').map((id) => id.trim()).filter(Boolean);
    return [];
};

let cachedAccountId: string | null = null;
let cachedUserIds: Set<string> | null = null;
let cachedPromise: Promise<Set<string>> | null = null;

export const resetActiveUserIdsCache = (): void => {
    cachedAccountId = null;
    cachedUserIds = null;
    cachedPromise = null;
};

export const getActiveUserIdsSet = async (accountId: string): Promise<Set<string>> => {
    if (cachedAccountId === accountId && cachedUserIds) return cachedUserIds;
    if (cachedAccountId === accountId && cachedPromise) return cachedPromise;

    cachedAccountId = accountId;
    cachedPromise = (async () => {
        const response = await accountUserApi.getActiveUserIds({accountId});
        const userIds = new Set(getParsedUserIds(getResponseBody<{user_ids?: unknown}>(response).user_ids));
        if (cachedAccountId === accountId) {
            cachedUserIds = userIds;
            cachedPromise = null;
        }
        return userIds;
    })();
    return cachedPromise;
};
